import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { AnalyticsAPIService } from 'app/api/analytics.api.service';
import { parseErrorMessage } from 'app/utils/callstate';
import { of } from 'rxjs';
import { catchError, filter, map, mergeMap, switchMap } from 'rxjs/operators';

import { AnalyticsActions } from './actions';
import { AppState } from 'app/reducers';
import { Store } from '@ngrx/store';

@Injectable()
export class AnalyticsEffects {
  loadAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.analyticsRequested),
      switchMap(({ query: { companyId, key, filter: queryFilter } }) =>
        this.analyticsApiService.Analytics(companyId, key, queryFilter).pipe(
          map((entries) => {
            const is = {
              costPay: { exist: false, name: 'Coût Paye' },
              axePay: { exist: false, name: 'Axe Paye' },
            };

            entries.forEach((entry) => {
              if (entry.name.match('Coût Paye') && entry.groupType === 'pay') {
                is.costPay.exist = true;
              }
              if (entry.name.match('Axe Paye') && entry.groupType === 'pay') {
                is.axePay.exist = true;
              }
            });

            Object.keys(is).forEach((k) => {
              if (!is[k].exist) {
                console.log(`Create requested ${is[k].name}`);
                this.store.dispatch(AnalyticsActions.analyticsEntryCreateRequested({
                  input: {
                    companyId,
                    axis: 'group',
                    name: is[k].name,
                    groupType: 'pay',
                    sortOrder: 1,
                    color: 'fuse-white-500',
                  },
                }));
              }
            });

            return AnalyticsActions.analyticsLoaded({ entries });
          }),
          catchError((error) =>
            of(
              AnalyticsActions.analyticsCancelled({
                errorMessage: parseErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  createAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.analyticsEntryCreateRequested),
      mergeMap(({ input }) =>
        this.analyticsApiService.CreateAnalyticsEntry(input).pipe(
          map((entry) => AnalyticsActions.analyticsEntryCreated({ entry })),
          catchError((error) =>
            of(
              AnalyticsActions.analyticsEntryCreateCancelled({
                errorMessage: parseErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  deleteAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.analyticsEntryDeleteRequested),
      mergeMap(({ input }) =>
        this.analyticsApiService.DeleteAnalyticsEntry(input).pipe(
          map((entry) => AnalyticsActions.analyticsEntryDeleted({ entry })),
          catchError((error) =>
            of(
              AnalyticsActions.analyticsEntryDeleteCancelled({
                errorMessage: parseErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  updateAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.analyticsEntryUpdateRequested),
      mergeMap(({ input }) =>
        this.analyticsApiService.UpdateAnalyticsEntry(input).pipe(
          map((entry) => AnalyticsActions.analyticsEntryUpdated({ entry })),
          catchError((error) =>
            of(
              AnalyticsActions.analyticsEntryUpdateCancelled({
                errorMessage: parseErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  updateSortAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.analyticsSortEntriesRequested),
      mergeMap(({ query, input }) =>
        this.analyticsApiService.UpdateAnalyticsEntrySort(input).pipe(
          map((_) => AnalyticsActions.analyticsRequested({ query })),
          catchError((error) =>
            of(
              AnalyticsActions.analyticsCancelled({
                errorMessage: parseErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );
  addGroupAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.analyticsEntryAddGroupRequested),
      mergeMap(({ input }) =>
        this.analyticsApiService.AddGroupAnalyticsEntry(input).pipe(
          map((entry) => AnalyticsActions.analyticsEntryGroupAdded({ entry })),
          catchError((error) =>
            of(
              AnalyticsActions.analyticsEntryAddGroupCancelled({
                errorMessage: parseErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  removeGroupAnalytics$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AnalyticsActions.analyticsEntryRemoveGroupRequested),
      mergeMap(({ input }) =>
        this.analyticsApiService.RemoveGroupAnalyticsEntry(input).pipe(
          map((entry) =>
            AnalyticsActions.analyticsEntryGroupRemoved({ entry })
          ),
          catchError((error) =>
            of(
              AnalyticsActions.analyticsEntryRemoveGroupCancelled({
                errorMessage: parseErrorMessage(error),
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private store: Store<AppState>,
    private actions$: Actions,
    private analyticsApiService: AnalyticsAPIService
  ) {}
}
